// eslint-disable no-non-orangelogic-images-eslint
import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4TabPanel } from "@/components/adg4/tabs/src/tab-panel";
import { Adg4Tabs } from "@/components/adg4/tabs/src/tabs";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useLocalizedImage } from "../use-localized-image";

import { TestimonialCard } from "./testimonial";

type SampleTabProps = {
    id: string;
    title: string;
    description: string;
    ctaText: string;
    ctaUrl: string;
    imageUrl: string;
    testimonialImage: string;
    testimonialAuthor: string;
    testimonialTitle: string;
    testimonialQuote: string;
};

const wrapperStyle = css({
    background: wacToken("color.neutral.light100"),
    color: wacToken("color.primary.black"),
    overflow: "hidden"
});

const slantedLineStyle = css({
    borderColor: `transparent ${wacToken("color.neutral.light0")} ${wacToken(
        "color.neutral.light100"
    )} transparent`,
    borderStyle: "solid",
    borderWidth: "0 100vw 20px 0",
    height: 0,
    width: 0
});

const headerContainerStyle = css({
    padding: "20px 0 0 0",
    textAlign: "center"
});

const tabOutlineOffset = "4px";

const headerStyle = css({
    display: "inline-block",
    textAlign: "center",
    fontWeight: "700",
    fontSize: "40px",
    lineHeight: "48px",
    maxWidth: "760px",
    marginBlockEnd: `calc(.83em - ${tabOutlineOffset})`,

    [media.only.md]: {
        fontSize: "36px",
        lineHeight: "42px",
        width: "536px"
    },

    [media.below.sm]: {
        fontSize: "32px",
        lineHeight: "38px",
        maxWidth: "330px"
    }
});

const tabList = css({
    padding: `${tabOutlineOffset} 16px 60px`,

    [media.below.sm]: {
        padding: `${tabOutlineOffset} 32px 30px`
    }
});

const tabStyles = css({
    "minWidth": 70,
    "backgroundColor": wacToken("color.neutral.light0"),
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "height": 43,
    "borderRadius": 28,
    "paddingInlineEnd": 30,
    "paddingInlineStart": 30,
    "cursor": "pointer",
    "border": `2px solid ${wacToken("color.neutral.light0")}`,
    "fontFamily": wacToken("font.charlie.text"),
    "fontWeight": "400",
    "fontSize": "20px",

    ":hover": {
        boxShadow: token("elevation.shadow.raised")
    },

    ":focus": {
        outlineOffset: `${tabOutlineOffset}`,
        outlineColor: wacToken("color.primary.black")
    }
});

const currentStyle = css({
    border: `2px solid ${wacToken("color.primary.blue")}`,
    backgroundColor: wacToken("color.extended.blue100")
});

const textBlockStyles = css({
    margin: "auto auto",
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h3: {
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px",
        margin: "0"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        fontWeight: 400,
        lineHeight: "24px",
        marginBlockStart: "8px !important"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        ":hover": {
            color: wacToken("color.primary.blue")
        }
    }
});

const SampleTab = ({
    title,
    isSelected
}: {
    title: string;
    isSelected?: boolean;
}) => {
    return (
        <button css={[tabStyles, isSelected && currentStyle]} className="h5">
            {title}
        </button>
    );
};

export const TabsSection = () => {
    const intl = useIntl();

    const shouldHideTabsSection = checkFeatureFlagGate(
        featureFlags.gwp7999JiraFeatures
    );

    const headerText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Header",
        defaultMessage:
            "Where devs, marketers, and every team in between get work done"
    });

    // Plan tab content
    const planLabelText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.Label",
        defaultMessage: "Plan"
    });
    const planBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.Body",
        defaultMessage:
            "Align teams, resources, and deliverables to ensure the project hits deadlines and maps to company goals from the start."
    });

    // Plan testimonial content
    const planTestimonialQuote = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.Quote",
        defaultMessage:
            "Work becomes a lot more visible when it's all in one place. It makes collaboration a whole lot easier."
    });
    const planTestimonialTitle = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.JobTitle",
        defaultMessage: "Internal Infrastructure, Canva",
        description: "Canva is a company."
    });
    const planTestimonialAuthor = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Plan.Author",
        defaultMessage: "Jeff Lai"
    });

    const planTestimonialImage =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/Pasted Graphic-2395339363.webp";

    // Track tab content
    const trackLabelText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Track.Label",
        defaultMessage: "Track"
    });
    const trackBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Track.Body",
        defaultMessage:
            "No matter how your team gets work done - Scrum, Kanban, or something in-between - Jira gives you visibility from start to finish."
    });

    // Track testimonial content
    const trackTestimonialQuote = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Track.Quote",
        defaultMessage:
            "The pace of delivery from idea to reality has exploded."
    });
    const trackTestimonialTitle = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Track.JobTitle",
        defaultMessage: "Agile Lead, Cancer Research UK",
        description: "Cancer Research UK is a company."
    });
    const trackTestimonialAuthor = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Track.Author",
        defaultMessage: "Greg Franklin"
    });
    const trackTestimonialImage =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/tab2image-29451184.webp";

    // Collaborate tab content
    const collaborateLabelText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Collaborate.Label",
        defaultMessage: "Collaborate"
    });
    const collaborateBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Collaborate.Body",
        defaultMessage:
            "Turn Jira into your team’s collaboration hub with real-time updates from popular 3rd party apps, commenting, Smart Links, and attachments."
    });

    // Collaborate testimonial content
    const collaborateTestimonialQuote = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Collaborate.Quote",
        defaultMessage:
            "People are practicing more knowledge-sharing than ever before, resulting in more transparency and efficiency."
    });
    const collaborateTestimonialTitle = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Collaborate.JobTitle",
        defaultMessage: "Business Analyst, Audi",
        description: "Audi is a company."
    });
    const collaborateTestimonialAuthor = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Collaborate.Author",
        defaultMessage: "Martin Binder"
    });

    const collaborateTestimonialImage =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/tab3image-29484945.webp";

    // Launch tab content
    const launchLabelText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Launch.Label",
        defaultMessage: "Launch"
    });
    const launchBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Launch.Body",
        defaultMessage:
            "Connect your go-to-market and software teams with shared release calendars, so launch day goes off without a hitch."
    });
    // Launch testimonial content
    const launchTestimonialQuote = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Launch.Quote",
        defaultMessage:
            "Less friction in the development process means better results and a faster time to market."
    });
    const launchTestimonialTitle = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Launch.JobTitle",
        defaultMessage: "Director of Engineering, Trulia",
        description: "Trulia is a company."
    });
    const launchTestimonialAuthor = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Launch.Author",
        defaultMessage: "Louis Bennet"
    });
    const launchTestimonialImage =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/tab4image-29379446.webp";

    // Report tab content
    const reportLabelText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.Label",
        defaultMessage: "Report"
    });
    const reportBodyText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.Body",
        defaultMessage:
            "Gather insights into work - before, during, and after the project - to confirm everything is on track, and pivot when it’s not."
    });

    // Report testimonial content
    const reportTestimonialQuote = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.Quote",
        defaultMessage:
            "Full visibility in Jira across the whole organization enables you to make better management judgment."
    });
    const reportTestimonialTitle = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.JobTitle",
        defaultMessage: "Digital Portfolio Director, Inchcape",
        description: "Inchcape is a company."
    });
    const reportTestimonialAuthor = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.Author",
        defaultMessage: "Paul Leaon"
    });
    const reportTestimonialImage =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/tab5image-29413207.webp";

    // shared CTA text
    const ctaTextJiraFree = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.TabsSection.CtaText",
        defaultMessage: "Try Jira free"
    });

    const sampleTabs: SampleTabProps[] = [
        {
            id: "plan",
            title: planLabelText,
            description: planBodyText,
            ctaText: ctaTextJiraFree,
            ctaUrl: useLocalizedHref("/software/jira/free"),
            imageUrl: useLocalizedImage("planLabelImage"),
            testimonialImage: planTestimonialImage,
            testimonialAuthor: planTestimonialAuthor,
            testimonialTitle: planTestimonialTitle,
            testimonialQuote: planTestimonialQuote
        },
        {
            id: "track",
            title: trackLabelText,
            description: trackBodyText,
            ctaText: ctaTextJiraFree,
            ctaUrl: useLocalizedHref("/software/jira/free"),
            imageUrl: useLocalizedImage("trackLabelImage"),
            testimonialImage: trackTestimonialImage,
            testimonialAuthor: trackTestimonialAuthor,
            testimonialTitle: trackTestimonialTitle,
            testimonialQuote: trackTestimonialQuote
        },
        {
            id: "collaborate",
            title: collaborateLabelText,
            description: collaborateBodyText,
            ctaText: ctaTextJiraFree,
            ctaUrl: useLocalizedHref("/software/jira/free"),
            imageUrl: useLocalizedImage("collaborateLabelImage"),
            testimonialImage: collaborateTestimonialImage,
            testimonialAuthor: collaborateTestimonialAuthor,
            testimonialTitle: collaborateTestimonialTitle,
            testimonialQuote: collaborateTestimonialQuote
        },
        {
            id: "launch",
            title: launchLabelText,
            description: launchBodyText,
            ctaText: ctaTextJiraFree,
            ctaUrl: useLocalizedHref("/software/jira/free"),
            imageUrl: useLocalizedImage("launchLabelImage"),
            testimonialImage: launchTestimonialImage,
            testimonialAuthor: launchTestimonialAuthor,
            testimonialTitle: launchTestimonialTitle,
            testimonialQuote: launchTestimonialQuote
        },
        {
            id: "report",
            title: reportLabelText,
            description: reportBodyText,
            ctaText: ctaTextJiraFree,
            ctaUrl: useLocalizedHref("/software/jira/free"),
            imageUrl: useLocalizedImage("reportLabelImage"),
            testimonialImage: reportTestimonialImage,
            testimonialAuthor: reportTestimonialAuthor,
            testimonialTitle: reportTestimonialTitle,
            testimonialQuote: reportTestimonialQuote
        }
    ];

    return (
        <div css={wrapperStyle}>
            <div css={slantedLineStyle} />
            <Row layout="12">
                <div css={headerContainerStyle}>
                    <h2 css={headerStyle}>{headerText}</h2>
                </div>
            </Row>

            {shouldHideTabsSection ? null : (
                <Adg4Tabs>
                    <Adg4DraggableTabList className="tab-list" css={tabList}>
                        {sampleTabs.map((tab, index) => (
                            <Adg4DraggableTab key={index} className="round-tab">
                                <SampleTab title={tab.title} />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {sampleTabs.map((tab, index) => (
                        <Adg4TabPanel key={index}>
                            <Row layout="4-8">
                                <div css={textBlockStyles}>
                                    <h3>{tab.title}</h3>
                                    <p>{tab.description}</p>
                                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                                    <Link
                                        text={tab.ctaText}
                                        type="primary"
                                        href={tab.ctaUrl}
                                    />
                                </div>
                                {getTabsSectionImage(tab.imageUrl)}
                            </Row>
                            <div>
                                <TestimonialCard
                                    image={tab.testimonialImage}
                                    quote={tab.testimonialQuote}
                                    name={tab.testimonialAuthor}
                                    title={tab.testimonialTitle}
                                />
                            </div>
                        </Adg4TabPanel>
                    ))}
                </Adg4Tabs>
            )}
        </div>
    );
};

export const getTabsSectionImage = (imageUrl: string) => {
    return (
        <div>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img
                alt=""
                src={imageUrl}
                css={css({
                    width: "100%",
                    height: "auto"
                })}
            />
        </div>
    );
};
